import React, { useState, useEffect } from 'react';
import { vulnerabilitiesAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  Shield,
  Plus,
  Loader2,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  Filter,
  Search,
  Upload,
  Download,
  Trash2,
  Edit3,
  MessageSquare,
  Calendar,
  User,
  X,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';

const SEVERITY_CONFIG = {
  critical: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', icon: AlertTriangle },
  high: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', icon: AlertCircle },
  medium: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', icon: AlertCircle },
  low: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', icon: Shield },
  info: { bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/30', icon: Shield },
};

const STATUS_CONFIG = {
  open: { bg: 'bg-red-500/20', text: 'text-red-400' },
  in_progress: { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  remediated: { bg: 'bg-green-500/20', text: 'text-green-400' },
  accepted: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  false_positive: { bg: 'bg-slate-500/20', text: 'text-slate-400' },
};

const VulnerabilityDashboard = () => {
  const { notify } = useNotification();
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedVuln, setSelectedVuln] = useState(null);
  const [filter, setFilter] = useState({ severity: 'all', status: 'all', search: '' });
  const [createForm, setCreateForm] = useState({
    title: '',
    severity: 'medium',
    status: 'open',
    cvssScore: 0,
    cveId: '',
    affectedAsset: '',
    description: '',
    remediation: '',
    dueDate: '',
    assignedTo: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vulnsRes, statsRes] = await Promise.all([
        vulnerabilitiesAPI.getAll(),
        vulnerabilitiesAPI.getStats()
      ]);
      setVulnerabilities(vulnsRes.data || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch vulnerabilities:', err);
      notify.error('Failed to load vulnerabilities');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await vulnerabilitiesAPI.create(createForm);
      notify.success('Vulnerability created');
      setShowCreateModal(false);
      setCreateForm({
        title: '',
        severity: 'medium',
        status: 'open',
        cvssScore: 0,
        cveId: '',
        affectedAsset: '',
        description: '',
        remediation: '',
        dueDate: '',
        assignedTo: ''
      });
      fetchData();
    } catch (err) {
      notify.error('Failed to create vulnerability');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (vulnId, newStatus) => {
    try {
      await vulnerabilitiesAPI.update(vulnId, { status: newStatus });
      notify.success('Status updated');
      fetchData();
    } catch (err) {
      notify.error('Failed to update status');
    }
  };

  const handleDelete = async (vulnId) => {
    if (!window.confirm('Are you sure you want to delete this vulnerability?')) return;
    try {
      await vulnerabilitiesAPI.delete(vulnId);
      notify.success('Vulnerability deleted');
      fetchData();
    } catch (err) {
      notify.error('Failed to delete vulnerability');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      await vulnerabilitiesAPI.bulkImport({
        vulnerabilities: data.vulnerabilities || data,
        source: 'import'
      });
      
      notify.success('Vulnerabilities imported successfully');
      setShowImportModal(false);
      fetchData();
    } catch (err) {
      notify.error('Failed to import vulnerabilities. Check file format.');
    }
  };

  const filteredVulns = vulnerabilities.filter(v => {
    if (filter.severity !== 'all' && v.severity !== filter.severity) return false;
    if (filter.status !== 'all' && v.status !== filter.status) return false;
    if (filter.search && !v.title.toLowerCase().includes(filter.search.toLowerCase()) &&
        !v.cveId?.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-16">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shield className="w-7 h-7 text-violet-400" />
              Vulnerability Management
            </h1>
            <p className="text-slate-400 mt-1">Track and remediate security vulnerabilities</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Vulnerability
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="text-2xl font-bold text-white">{stats.total}</div>
              <div className="text-sm text-slate-400">Total</div>
            </div>
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
              <div className="text-2xl font-bold text-red-400">{stats.bySeverity.critical}</div>
              <div className="text-sm text-red-300">Critical</div>
            </div>
            <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/30">
              <div className="text-2xl font-bold text-orange-400">{stats.bySeverity.high}</div>
              <div className="text-sm text-orange-300">High</div>
            </div>
            <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
              <div className="text-2xl font-bold text-yellow-400">{stats.byStatus.open}</div>
              <div className="text-sm text-yellow-300">Open</div>
            </div>
            <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
              <div className="text-2xl font-bold text-green-400">{stats.byStatus.remediated}</div>
              <div className="text-sm text-green-300">Remediated</div>
            </div>
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
              <div className="text-2xl font-bold text-red-400">{stats.overdue}</div>
              <div className="text-sm text-red-300">Overdue</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search vulnerabilities..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <select
            value={filter.severity}
            onChange={(e) => setFilter({ ...filter, severity: e.target.value })}
            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="info">Info</option>
          </select>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="remediated">Remediated</option>
            <option value="accepted">Risk Accepted</option>
            <option value="false_positive">False Positive</option>
          </select>
        </div>

        {/* Vulnerabilities List */}
        {filteredVulns.length === 0 ? (
          <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
            <Shield className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Vulnerabilities Found</h3>
            <p className="text-slate-400 mb-6">Add vulnerabilities manually or import from a scanner.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
            >
              Add First Vulnerability
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredVulns.map((vuln) => {
              const sevConfig = SEVERITY_CONFIG[vuln.severity] || SEVERITY_CONFIG.medium;
              const statusConfig = STATUS_CONFIG[vuln.status] || STATUS_CONFIG.open;
              const SevIcon = sevConfig.icon;

              return (
                <div
                  key={vuln.id}
                  className={`bg-slate-800/50 rounded-xl p-6 border ${sevConfig.border} hover:border-violet-500/50 transition-all`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sevConfig.bg}`}>
                        <SevIcon className={`w-5 h-5 ${sevConfig.text}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-white">{vuln.title}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${sevConfig.bg} ${sevConfig.text}`}>
                            {vuln.severity.toUpperCase()}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${statusConfig.bg} ${statusConfig.text}`}>
                            {vuln.status.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                          {vuln.cveId && (
                            <a 
                              href={`https://nvd.nist.gov/vuln/detail/${vuln.cveId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 hover:text-violet-400"
                            >
                              {vuln.cveId}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                          {vuln.cvssScore > 0 && (
                            <span>CVSS: {vuln.cvssScore}</span>
                          )}
                          {vuln.affectedAsset && (
                            <span>Asset: {vuln.affectedAsset}</span>
                          )}
                          {vuln.dueDate && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Due: {new Date(vuln.dueDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {vuln.description && (
                          <p className="mt-2 text-slate-300 text-sm line-clamp-2">{vuln.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={vuln.status}
                        onChange={(e) => handleUpdateStatus(vuln.id, e.target.value)}
                        className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white focus:outline-none"
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="remediated">Remediated</option>
                        <option value="accepted">Risk Accepted</option>
                        <option value="false_positive">False Positive</option>
                      </select>
                      <button
                        onClick={() => handleDelete(vuln.id)}
                        className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-2xl border border-slate-700 shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-700 sticky top-0 bg-slate-800">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Add Vulnerability</h3>
                  <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreate} className="p-6 space-y-4">
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Title *</label>
                  <input
                    type="text"
                    value={createForm.title}
                    onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Severity</label>
                    <select
                      value={createForm.severity}
                      onChange={(e) => setCreateForm({ ...createForm, severity: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    >
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                      <option value="info">Info</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">CVSS Score</label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={createForm.cvssScore}
                      onChange={(e) => setCreateForm({ ...createForm, cvssScore: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">CVE ID</label>
                    <input
                      type="text"
                      placeholder="CVE-2024-XXXXX"
                      value={createForm.cveId}
                      onChange={(e) => setCreateForm({ ...createForm, cveId: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Affected Asset</label>
                    <input
                      type="text"
                      value={createForm.affectedAsset}
                      onChange={(e) => setCreateForm({ ...createForm, affectedAsset: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-1">Description</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-1">Remediation Steps</label>
                  <textarea
                    value={createForm.remediation}
                    onChange={(e) => setCreateForm({ ...createForm, remediation: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Due Date</label>
                    <input
                      type="date"
                      value={createForm.dueDate}
                      onChange={(e) => setCreateForm({ ...createForm, dueDate: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Assigned To</label>
                    <input
                      type="text"
                      value={createForm.assignedTo}
                      onChange={(e) => setCreateForm({ ...createForm, assignedTo: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !createForm.title}
                    className="flex-1 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700 shadow-xl">
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Import Vulnerabilities</h3>
                  <button onClick={() => setShowImportModal(false)} className="text-slate-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <p className="text-slate-400 text-sm">
                  Upload a JSON file with vulnerability data. Expected format:
                </p>
                <pre className="bg-slate-900 p-3 rounded-lg text-xs text-slate-300 overflow-x-auto">
{`{
  "vulnerabilities": [
    {
      "title": "SQL Injection",
      "severity": "high",
      "cveId": "CVE-2024-1234",
      "affectedAsset": "Web Server",
      "description": "...",
      "remediation": "..."
    }
  ]
}`}
                </pre>
                <label className="block">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImport}
                    className="hidden"
                  />
                  <div className="flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-violet-500 hover:text-violet-400 cursor-pointer transition-colors">
                    <Upload className="w-5 h-5" />
                    Click to select JSON file
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default VulnerabilityDashboard;
